import type { PostgrestError } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { deleteTimelineEventImage } from "@/features/timeline/timelineStorage";
import {
  parseTimelineEventDraft,
  type TimelineCapacitySummary,
  type TimelineEventDraft,
  type TimelineEventView,
  type TimelineLimitPrompt,
  type TimelineLinkTargetOption,
} from "@/features/timeline";
import type { SubscriptionTier } from "@/types/projects";

type ProjectRow = Pick<Database["public"]["Tables"]["projects"]["Row"], "id" | "title">;
type TimelineEventRow = Database["public"]["Tables"]["timeline_events"]["Row"];
type TimelineEventLinkRow = Database["public"]["Tables"]["timeline_event_links"]["Row"];

export type TimelineEntityTargetRow = Pick<
  Database["public"]["Tables"]["entity_cards"]["Row"],
  "id" | "name" | "type"
>;

export type TimelineLoreTargetRow = Pick<
  Database["public"]["Tables"]["lore_entries"]["Row"],
  "canon_status" | "id" | "title" | "type"
>;

export type TimelineStoryRow = Pick<
  Database["public"]["Tables"]["stories"]["Row"],
  "id" | "mode" | "title" | "type"
>;

export type TimelineChapterRow = Pick<
  Database["public"]["Tables"]["chapters"]["Row"],
  "id" | "sort_order" | "story_id" | "title"
>;

export interface TimelineWorkspaceSource {
  capacity: TimelineCapacitySummary;
  chapterRows: TimelineChapterRow[];
  entityRows: TimelineEntityTargetRow[];
  eventRows: TimelineEventRow[];
  linkRows: TimelineEventLinkRow[];
  loreRows: TimelineLoreTargetRow[];
  project: ProjectRow;
  storyRows: TimelineStoryRow[];
}

export type TimelineActionErrorCode =
  | "generic"
  | "missing-config"
  | "access-denied"
  | "not-found"
  | "limit-reached"
  | "link-invalid"
  | "reorder-failed";

interface TimelineActionErrorOptions {
  cause?: unknown;
  limitPrompt?: TimelineLimitPrompt | null;
}

export class TimelineActionError extends Error {
  readonly cause?: unknown;
  readonly limitPrompt?: TimelineLimitPrompt | null;

  constructor(
    public readonly code: TimelineActionErrorCode,
    message?: string,
    options?: TimelineActionErrorOptions,
  ) {
    super(message ?? code);
    this.name = "TimelineActionError";
    this.cause = options?.cause;
    this.limitPrompt = options?.limitPrompt ?? null;
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new TimelineActionError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

export function parseTimelineLimitError(message: string): TimelineLimitPrompt | null {
  const [prefix, tier, currentCount, allowedCount, recommendedTier] = message.split("|");

  if (
    prefix !== "TIMELINE_EVENT_LIMIT_REACHED"
    || !tier
    || !currentCount
    || !allowedCount
    || !recommendedTier
  ) {
    return null;
  }

  return {
    allowedCount: Number(allowedCount),
    currentCount: Number(currentCount),
    reason: "timeline_event_limit_reached",
    recommendedTier: recommendedTier as SubscriptionTier,
    tier: tier as SubscriptionTier,
  };
}

function mapTimelineCapacityRow(
  row: Database["public"]["Functions"]["get_project_timeline_capacity"]["Returns"][number],
): TimelineCapacitySummary {
  return {
    eventsLimit: row.timeline_events_limit,
    eventsRemaining: row.timeline_events_remaining,
    eventsUsed: row.timeline_events_used,
    isUnlimited: row.is_unlimited,
    tier: row.tier,
  };
}

function mapTimelineError(error: unknown): TimelineActionError {
  if (error instanceof TimelineActionError) {
    return error;
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message)
      : "Timeline action failed.";

  const limitPrompt = parseTimelineLimitError(message);
  if (limitPrompt) {
    return new TimelineActionError("limit-reached", message, {
      cause: error,
      limitPrompt,
    });
  }

  if (message === "TIMELINE_LINK_INVALID") {
    return new TimelineActionError("link-invalid", message, { cause: error });
  }

  if (
    message === "TIMELINE_REORDER_INCOMPLETE"
    || message === "TIMELINE_REORDER_INVALID"
  ) {
    return new TimelineActionError("reorder-failed", message, { cause: error });
  }

  const postgrestError = error as PostgrestError | undefined;
  if (postgrestError?.code === "PGRST116") {
    return new TimelineActionError("not-found", "That timeline record is unavailable.", {
      cause: error,
    });
  }

  if (
    postgrestError?.code === "42501"
    || message === "PROJECT_ACCESS_DENIED"
    || message.toLowerCase().includes("row-level security")
  ) {
    return new TimelineActionError("access-denied", "You do not have access to that timeline.", {
      cause: error,
    });
  }

  return new TimelineActionError("generic", message, { cause: error });
}

export function formatTimelineErrorMessage(error: unknown) {
  const timelineError = mapTimelineError(error);

  switch (timelineError.code) {
    case "missing-config":
      return "timeline.messages.missingConfig";
    case "access-denied":
      return "timeline.messages.accessDenied";
    case "not-found":
      return "timeline.messages.notFound";
    case "limit-reached":
      return "timeline.messages.limitReached";
    case "link-invalid":
      return "timeline.messages.linkInvalid";
    case "reorder-failed":
      return "timeline.messages.reorderFailed";
    default:
      return "timeline.messages.loadError";
  }
}

export function getTimelineLimitPrompt(error: unknown) {
  return mapTimelineError(error).limitPrompt ?? null;
}

async function getNextTimelineSortOrder(projectId: string) {
  const { data, error } = await supabase
    .from("timeline_events")
    .select("sort_order")
    .eq("project_id", projectId)
    .order("sort_order", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw mapTimelineError(error);
  }

  return typeof data?.sort_order === "number" ? data.sort_order + 1 : 0;
}

export async function getProjectTimelineCapacity(
  projectId: string,
): Promise<TimelineCapacitySummary> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_project_timeline_capacity", {
    target_project_id: projectId,
  });

  if (error) {
    throw mapTimelineError(error);
  }

  const capacityRow = data?.[0];
  if (!capacityRow) {
    throw new TimelineActionError("not-found", "That project is unavailable.");
  }

  return mapTimelineCapacityRow(capacityRow);
}

export function buildImportedTimelineEventView(input: {
  displayTitle: string;
  elementData: Record<string, unknown>;
  projectId: string;
}): TimelineEventView {
  const now = new Date().toISOString();
  const title =
    typeof input.elementData.title === "string" && input.elementData.title.trim().length > 0
      ? input.elementData.title
      : input.displayTitle;
  const description =
    typeof input.elementData.description === "string" ? input.elementData.description : "";

  return {
    aiSummary: null,
    aiSummaryGeneratedAt: null,
    brokenLinkCount: 0,
    createdAt: now,
    description,
    descriptionPreview: description,
    eraLabel: typeof input.elementData.eraLabel === "string" ? input.elementData.eraLabel : "",
    hasApprovedSummary: false,
    id: "import-review-timeline-event",
    imagePath: null,
    imageUrl: null,
    linkCount: 0,
    links: [],
    projectId: input.projectId,
    sortOrder:
      typeof input.elementData.sortOrder === "number" ? input.elementData.sortOrder : 0,
    title,
    updatedAt: now,
  };
}

export function buildImportedTimelineEventElementData(
  draft: TimelineEventDraft,
  baseElementData: Record<string, unknown> = {},
) {
  return {
    ...baseElementData,
    description: draft.description,
    eraLabel: draft.eraLabel,
    title: draft.title,
  };
}

export async function loadTimelineWorkspaceSource(
  projectId: string,
): Promise<TimelineWorkspaceSource> {
  ensureConfigured();

  const [projectResult, capacity, eventResult, entityResult, loreResult, storyResult] =
    await Promise.all([
      supabase.from("projects").select("id, title").eq("id", projectId).maybeSingle(),
      getProjectTimelineCapacity(projectId),
      supabase
        .from("timeline_events")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
      supabase
        .from("entity_cards")
        .select("id, name, type")
        .eq("project_id", projectId)
        .order("name", { ascending: true }),
      supabase
        .from("lore_entries")
        .select("id, title, type, canon_status")
        .eq("project_id", projectId)
        .order("title", { ascending: true }),
      supabase
        .from("stories")
        .select("id, title, type, mode")
        .eq("project_id", projectId),
    ]);

  if (projectResult.error) {
    throw mapTimelineError(projectResult.error);
  }

  if (eventResult.error) {
    throw mapTimelineError(eventResult.error);
  }

  if (entityResult.error) {
    throw mapTimelineError(entityResult.error);
  }

  if (loreResult.error) {
    throw mapTimelineError(loreResult.error);
  }

  if (storyResult.error) {
    throw mapTimelineError(storyResult.error);
  }

  const project = projectResult.data as ProjectRow | null;
  if (!project) {
    throw new TimelineActionError("not-found", "That project is unavailable.");
  }

  const eventRows = ((eventResult.data as TimelineEventRow[] | null) ?? []);
  const eventIds = eventRows.map((row) => row.id);

  const linkResult = eventIds.length === 0
    ? { data: [] as TimelineEventLinkRow[], error: null }
    : await supabase
      .from("timeline_event_links")
      .select("*")
      .in("event_id", eventIds);

  if (linkResult.error) {
    throw mapTimelineError(linkResult.error);
  }

  const storyRows = ((storyResult.data as TimelineStoryRow[] | null) ?? []);
  const chapterStoryIds = storyRows
    .filter((story) => story.type === "main" && story.mode === "chapters")
    .map((story) => story.id);

  const chapterResult = chapterStoryIds.length === 0
    ? { data: [] as TimelineChapterRow[], error: null }
    : await supabase
      .from("chapters")
      .select("id, title, sort_order, story_id")
      .in("story_id", chapterStoryIds)
      .order("sort_order", { ascending: true });

  if (chapterResult.error) {
    throw mapTimelineError(chapterResult.error);
  }

  return {
    capacity,
    chapterRows: (chapterResult.data as TimelineChapterRow[] | null) ?? [],
    entityRows: (entityResult.data as TimelineEntityTargetRow[] | null) ?? [],
    eventRows,
    linkRows: (linkResult.data as TimelineEventLinkRow[] | null) ?? [],
    loreRows: (loreResult.data as TimelineLoreTargetRow[] | null) ?? [],
    project,
    storyRows,
  };
}

export async function createTimelineEvent(
  projectId: string,
  draft: TimelineEventDraft,
) {
  ensureConfigured();
  const validated = parseTimelineEventDraft(draft);
  const sortOrder = await getNextTimelineSortOrder(projectId);

  const { data, error } = await supabase
    .from("timeline_events")
    .insert({
      description: validated.description,
      era_label: validated.eraLabel,
      project_id: projectId,
      sort_order: sortOrder,
      title: validated.title,
    })
    .select("*")
    .single();

  if (error) {
    throw mapTimelineError(error);
  }

  return data as TimelineEventRow;
}

export async function updateTimelineEvent(
  eventId: string,
  draft: TimelineEventDraft,
) {
  ensureConfigured();
  const validated = parseTimelineEventDraft(draft);

  const { data, error } = await supabase
    .from("timeline_events")
    .update({
      description: validated.description,
      era_label: validated.eraLabel,
      title: validated.title,
    })
    .eq("id", eventId)
    .select("*")
    .single();

  if (error) {
    throw mapTimelineError(error);
  }

  return data as TimelineEventRow;
}

export async function deleteTimelineEvent(eventId: string) {
  ensureConfigured();

  const { error } = await supabase
    .from("timeline_events")
    .delete()
    .eq("id", eventId);

  if (error) {
    throw mapTimelineError(error);
  }
}

export async function removeTimelineEventImage(eventId: string, imagePath: string) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("timeline_events")
    .update({
      image_path: null,
    })
    .eq("id", eventId)
    .select("*")
    .single();

  if (error) {
    throw mapTimelineError(error);
  }

  await deleteTimelineEventImage(imagePath);

  return data as TimelineEventRow;
}

export async function addTimelineEventLink(
  eventId: string,
  option: TimelineLinkTargetOption,
) {
  ensureConfigured();

  const { error } = await supabase
    .from("timeline_event_links")
    .insert({
      event_id: eventId,
      linked_id: option.id,
      linked_kind: option.kind,
    });

  if (error) {
    throw mapTimelineError(error);
  }
}

export async function removeTimelineEventLink(linkId: string) {
  ensureConfigured();

  const { error } = await supabase
    .from("timeline_event_links")
    .delete()
    .eq("id", linkId);

  if (error) {
    throw mapTimelineError(error);
  }
}

export async function reorderTimelineEvents(
  projectId: string,
  orderedEventIds: string[],
) {
  ensureConfigured();

  const { error } = await supabase.rpc("reorder_timeline_events", {
    ordered_event_ids: orderedEventIds,
    target_project_id: projectId,
  });

  if (error) {
    throw mapTimelineError(error);
  }
}
